import { useMap } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type Viewport } from '../api/studentNetworkV2Api.types';
import { STUDENT_NETWORK_MAP_ID } from '../constants/mapConstants';

type MapViewportState = {
    viewports: Viewport[];
    zoom: number;
} | null;

const VIEWPORT_PADDING_DEGREES = 10;
const DEBOUNCE_MS = 500;

function padBounds(bounds: google.maps.LatLngBounds): google.maps.LatLngBounds {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const paddedSw = new google.maps.LatLng(
        Math.max(-85, sw.lat() - VIEWPORT_PADDING_DEGREES),
        sw.lng() - VIEWPORT_PADDING_DEGREES,
    );
    const paddedNe = new google.maps.LatLng(
        Math.min(85, ne.lat() + VIEWPORT_PADDING_DEGREES),
        ne.lng() + VIEWPORT_PADDING_DEGREES,
    );
    return new google.maps.LatLngBounds(paddedSw, paddedNe);
}

function boundsToViewports(bounds: google.maps.LatLngBounds): Viewport[] {
    const padded = padBounds(bounds);
    const sw = padded.getSouthWest();
    const ne = padded.getNorthEast();
    const lng1 = sw.lng();
    const lat1 = sw.lat();
    const lng2 = ne.lng();
    const lat2 = ne.lat();

    const crossesDateLine = lng1 > lng2;

    // -179.999 instead of -180: avoids an exact-boundary edge case where the
    // API treats -180 == 180 and returns no results for the wrapped segment.
    const viewports: Viewport[] = [[crossesDateLine ? -179.999 : lng1, lat1, lng2, lat2]];

    if (crossesDateLine) {
        viewports.push([lng1, lat1, 180, lat2]);
    }

    return viewports;
}

export function useMapViewport(): MapViewportState {
    const map = useMap(STUDENT_NETWORK_MAP_ID);
    const [viewport, setViewport] = useState<MapViewportState>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasInitialViewportRef = useRef(false);

    const updateViewport = useCallback(() => {
        if (!map) return;
        const bounds = map.getBounds();
        if (!bounds) return;

        const zoom = map.getZoom() ?? 3;
        const viewports = boundsToViewports(bounds);
        setViewport({ viewports, zoom });
        hasInitialViewportRef.current = true;
    }, [map]);

    useEffect(() => {
        if (!map) return undefined;

        hasInitialViewportRef.current = false;

        // Debounced idle listener for pan/zoom changes
        const idleListener = map.addListener('idle', () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(updateViewport, DEBOUNCE_MS);
        });

        // bounds_changed fires when bounds first become available — critical
        // on remount when the map is already idle and won't fire idle again.
        const boundsListener = map.addListener('bounds_changed', () => {
            if (!hasInitialViewportRef.current) updateViewport();
        });

        // Eagerly try right now (bounds are often available immediately)
        updateViewport();

        return () => {
            idleListener.remove();
            boundsListener.remove();
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [map, updateViewport]);

    return viewport;
}
